"use client";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Search, Package } from "lucide-react";
import { Button } from "@/app/components/shadcn/button";
import { Input } from "@/app/components/shadcn/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/app/components/shadcn/form";
import { PhoneInput } from "@/app/components/elements/forms/phoneInput/PhoneInput";
import {
  shippingTrackingSchema,
  ShippingTrackingData,
} from "@/app/schemas/shippingTrackingSchema";
import { CargoTrackingMetadata } from "@/app/lib/features/shippingTracking/shippingTracking.types";

interface ShippingTrackingFormProps {
  metadata: CargoTrackingMetadata;
  isLoading: boolean;
  onSubmit: (data: ShippingTrackingData) => void;
}

const ShippingTrackingForm = ({
  metadata,
  isLoading,
  onSubmit,
}: ShippingTrackingFormProps) => {
  const orderField = metadata.form.find((f) => f.id === "order_number");
  const phoneField = metadata.form.find((f) => f.id === "phone_number");

  const form = useForm<ShippingTrackingData>({
    resolver: zodResolver(shippingTrackingSchema),
    defaultValues: { orderNumber: "", phoneNumber: "" },
    mode: "onChange",
  });

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 lg:p-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {orderField && (
            <FormField
              control={form.control}
              name="orderNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-medium text-gray-900">
                    {orderField.title}
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Package className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <Input
                        placeholder={orderField.placeholder}
                        className="pl-10 h-12 text-base"
                        {...field}
                        onChange={(e) => {
                          field.onChange(
                            e.target.value
                              .replace(/[^A-Za-z0-9\-]/g, "")
                              .toUpperCase(),
                          );
                        }}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                  <p className="text-sm text-gray-500">{orderField.subtitle}</p>
                </FormItem>
              )}
            />
          )}

          {phoneField && (
            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-medium text-gray-900">
                    {phoneField.title}
                  </FormLabel>
                  <FormControl>
                    <PhoneInput
                      value={field.value}
                      onChange={field.onChange}
                      className="h-12"
                    />
                  </FormControl>
                  <FormMessage />
                  <p className="text-sm text-gray-500">{phoneField.subtitle}</p>
                </FormItem>
              )}
            />
          )}

          <Button
            type="submit"
            className="w-full h-12 text-base font-semibold"
            disabled={isLoading}
          >
            <Search className="w-5 h-5 mr-2" />
            {isLoading ? "Sorgulanıyor..." : metadata.button_text}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default ShippingTrackingForm;
